import { type OutlierLapInput } from './lapOutlierExclusion'
import {
  LAP_DELTA_BAND_PCT,
  LAP_DELTA_AMBER_FRACTION,
} from '../config/lapFiltering'
import { MS_PER_S } from '../../utils/timeUnits'

export interface LapTimeDelta {
  lapId: string
  /** lapTimeMs − mean of the currently-included laps' times. */
  deltaMs: number
  band: 'green' | 'amber' | 'red'
  /** BINDING display format: '+1.9s' | '-0.3s' | '+0.0s'. */
  label: string
}

/**
 * Live lap-time deltas for the Filter Laps drawer. The baseline is the MEAN
 * lap time of the currently-included laps, so the map adapts as the user
 * toggles checkboxes; excluded laps get a delta against the same mean so
 * re-including them is an informed choice.
 */
export function buildLapTimeDeltas(
  laps: OutlierLapInput[],
  includedLapIds: Set<string>
): Map<string, LapTimeDelta> {
  const deltas = new Map<string, LapTimeDelta>()

  const validLaps = laps.filter(
    (l): l is { lapId: string; lapTimeMs: number } =>
      l.lapTimeMs != null && l.lapTimeMs > 0
  )
  const includedTimes = validLaps
    .filter((l) => includedLapIds.has(l.lapId))
    .map((l) => l.lapTimeMs)
  if (includedTimes.length === 0) return deltas

  const mean =
    includedTimes.reduce((sum, t) => sum + t, 0) / includedTimes.length
  const tolMs = (mean * LAP_DELTA_BAND_PCT) / 100

  validLaps.forEach(({ lapId, lapTimeMs }) => {
    const deltaMs = lapTimeMs - mean
    const band =
      deltaMs > tolMs
        ? 'red'
        : deltaMs > tolMs * LAP_DELTA_AMBER_FRACTION
          ? 'amber'
          : 'green'
    const label = `${deltaMs >= 0 ? '+' : '-'}${(Math.abs(deltaMs) / MS_PER_S).toFixed(1)}s`
    deltas.set(lapId, { lapId, deltaMs, band, label })
  })
  return deltas
}
